import cn from 'classnames';
import { Scenario } from 'context-system-model';
import { unionBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import {
  fetchScenarios,
  getAvailableGroups,
  ContextualBiddingList as List,
  ContextualBiddingModalGroups as ModalGroups,
  ContextualBiddingModalScenarios as ModalScenarios,
  ScenarioModalCreator,
  ScenarioModalEdit,
  ScenarioModalViewer
} from 'sonata-platform-components';
import {
  ButtonUI,
  ContextualMenu,
  CustomThemeProvider,
  InputSearch,
  InventoryDistributionToggle,
  LoadingSkeleton,
  Prompt,
  RadioButtonsGroup,
  Range,
  Switch,
  usePortal,
  useQueryFetch
} from 'sonata-react-components';
import { isDigitalCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { normalize, normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { FormExpandableSection } from '@/components/common/FormExpandableSection';
import { ContextualBiddingSectionStyle as ContextualBiddingStyled } from './contextualBiddingSection.style';
import { getContextualBiddingTags } from './helpers/getSectionHeaderTags.helper';

import type { MultiFlightEntityType } from 'sonata-static-storage/lib/common/common.types';
import type { ContextualBiddingInterface } from 'sonata-static-storage/lib/schemas/campaign/contextualBidding.schema';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

type ButtonUIProps = React.ComponentProps<typeof ButtonUI>;
type ScenarioModalEditProps = React.ComponentProps<typeof ScenarioModalEdit>;
type Props = {
  campaign: ScenarioModalEditProps['campaign'];
  contextualBidding: ContextualBiddingInterface<any>;
  currencyInfo: React.ComponentProps<typeof InventoryDistributionToggle>['currencyInfo'];
  entityType?: MultiFlightEntityType;
  goal?: number;
  groupsConfig: {
    groups: Record<string, any>[];
    items: Record<string, any>[];
  };
  id: string;
  onChange: (contextualBidding: ContextualBiddingInterface<string>) => void;
  theme?: 'light' | 'dark';
  user: Record<string, any>;
  userRoles?: UserRoleInterface[];
};

type State = {
  groupedScenarios: Map<string, Props['groupsConfig']['groups']>;
  newScenario?: Scenario;
  scenarioCreationOpen: boolean;
  scenarioEditionOpen: boolean;
  scenarioGroupsModal: {
    id: string;
    status: 'closed' | 'opened';
  };
  scenarioIdToDelete?: string;
  scenarioSelected: Scenario | null;
  scenarioViewerOpen: boolean;
  scenarios: Scenario[];
  scenariosSelectorOpen: boolean;
  showDeletionPrompt: boolean;
};

type RadioButton = 'with_context' | 'with_no_context' | 'balanced' | '';

type UiState = {
  inputText: string;
  rangeKey: number;
  selectedRadioButton: RadioButton;
};

type CampaignGroups = Props['groupsConfig']['groups'];

const DEFAULT_STATE_VALUE: State = {
  groupedScenarios: new Map(),
  scenarioCreationOpen: false,
  scenarioEditionOpen: false,
  scenarioGroupsModal: {
    id: '',
    status: 'closed'
  },
  scenarioSelected: null,
  scenarioViewerOpen: false,
  scenarios: [],
  scenariosSelectorOpen: false,
  showDeletionPrompt: false
};

export function ContextualBidding({
  campaign,
  contextualBidding,
  currencyInfo,
  entityType = 'insertionOrder',
  goal = 0,
  groupsConfig,
  id,
  onChange,
  theme = 'light',
  user,
  userRoles = []
}: Props) {
  const { formatMessage } = useIntl();
  const groupsType = isDigitalCampaign(campaign.type) ? 'zoneGroups' : 'screenGroups';
  const { countries } = campaign;

  const rangeValue = contextualBidding?.distribution?.value ?? 0.5;

  const valueByRadioButton = {
    balanced: goal / 2,
    with_context: goal as number,
    with_no_context: 0
  } as Record<RadioButton, number>;

  const [
    {
      groupedScenarios,
      scenarioCreationOpen,
      scenarioEditionOpen,
      scenarioGroupsModal,
      scenarioIdToDelete,
      scenarioSelected,
      scenarioViewerOpen,
      scenarios,
      scenariosSelectorOpen,
      showDeletionPrompt
    },
    setState
  ] = useState<State>({ ...DEFAULT_STATE_VALUE });
  const [{ inputText, rangeKey, selectedRadioButton }, setUiState] = useState<UiState>(() => ({
    inputText: '',
    rangeKey: 0,
    selectedRadioButton:
      (Object.entries(valueByRadioButton).find(([, v]) => v === rangeValue)?.[0] as RadioButton) || ''
  }));

  const targetBodyPrompt = usePortal('contextScenarioList_deletePrompt');
  const hasPermissionsToCreate = checkUserResourcePermissions('contextBuilderCreateContext', user, userRoles);
  const hasAccessToAddGroups = checkUserResourcePermissions('contextBuilderGroups', user, userRoles);
  const hasAccessToEditScenarios = checkUserResourcePermissions('contextBuilderEditContext', user, userRoles);
  const hasAccessToContextualBiddingInventoryDistribution = checkUserResourcePermissions(
    'contextBuilderContextualBiddingInventoryDistribution',
    user,
    userRoles
  );

  const onViewScenario = (_id: string) => {
    setState(prevState => ({
      ...prevState,
      scenarioSelected: scenarios.find(scenario => scenario._id === _id) || null,
      scenarioViewerOpen: true
    }));
  };

  const handleEditionClick = (_id: string) => {
    setState(prevState => ({
      ...prevState,
      scenarioEditionOpen: true,
      scenarioSelected: scenarios.find(scenario => scenario._id === _id) || null
    }));
  };

  const handleSelectContentClick = () => {
    setState(prevState => ({
      ...prevState,
      scenariosSelectorOpen: true
    }));
  };

  const handleScenarioCreationToggle = (status: 'opened' | 'closed') => {
    setState(prevState => ({
      ...prevState,
      scenarioCreationOpen: status === 'opened'
    }));
  };

  const handleChangeCampaign = (changedScenarios: Scenario[]) => {
    const newContextBiddingScenarios: ContextualBiddingInterface<string>['scenarios'] = changedScenarios.map(
      scenario => {
        const existingContextBiddingConfiguration = contextualBidding?.scenarios.find(c => c.scenario === scenario._id);
        return existingContextBiddingConfiguration
          ? {
              ...existingContextBiddingConfiguration,
              removed: false
            }
          : {
              removed: false,
              scenario: scenario._id!,
              screenGroups: [],
              zoneGroups: []
            };
      }
    );
    const newGroupedScenarios = structuredClone(groupedScenarios);
    newContextBiddingScenarios.forEach(nCS => {
      if (!newGroupedScenarios.has(nCS.scenario)) {
        newGroupedScenarios.set(nCS.scenario, []);
      }
    });
    onChange({
      ...contextualBidding,
      enabled: true,
      scenarios: contextualBidding.enabled
        ? unionBy(newContextBiddingScenarios, contextualBidding.scenarios, 'scenario')
        : newContextBiddingScenarios
    });
    setState(prevState => ({
      ...prevState,
      groupedScenarios: newGroupedScenarios,
      scenarios: changedScenarios,
      scenariosSelectorOpen: false
    }));
  };

  const handleScenarioCreationSave = (createdScenario: Scenario) => {
    handleChangeCampaign(scenarios.concat(createdScenario));
    handleScenarioCreationToggle('closed');
  };

  const onSaveSelectedContext = (selectedScenarios: Scenario[]) => {
    const newScenarios = selectedScenarios.map(selectedScenario => {
      const scenario: any = scenarios.find(item => item._id === selectedScenario._id);
      return scenario
        ? {
            ...selectedScenario,
            groups: scenario.groups
          }
        : selectedScenario;
    });
    handleChangeCampaign(newScenarios);
  };

  const onSelectScenarioModalClose = () => {
    setState(prevState => ({
      ...prevState,
      scenariosSelectorOpen: false
    }));
  };

  const onViewScenarioModalClose = () => {
    setState(prevState => ({
      ...prevState,
      scenarioViewerOpen: false
    }));
  };

  const handleEditScenarioModalClose = () => {
    setState(prevState => ({
      ...prevState,
      scenarioEditionOpen: false
    }));
  };

  const onDeleteHandler = (_id: string) => {
    setState(prevState => ({
      ...prevState,
      scenarioIdToDelete: _id,
      showDeletionPrompt: true
    }));
  };

  const markScenarioAsRemoved: (scenarioId: string) => ContextualBiddingInterface<string>['scenarios'] = scenarioId => {
    const index = contextualBidding.scenarios.findIndex(sce => sce.scenario === scenarioId);
    const contextualBiddingScenarios = structuredClone(contextualBidding.scenarios);
    contextualBiddingScenarios[index] = {
      ...contextualBiddingScenarios[index],
      removed: true,
      screenGroups: [],
      zoneGroups: []
    };
    return contextualBiddingScenarios;
  };

  const onDeleteScenario = () => {
    const newGroupedScenarios = structuredClone(groupedScenarios);
    newGroupedScenarios.delete(scenarioIdToDelete!);
    setState(prevState => ({
      ...prevState,
      groupedScenarios: newGroupedScenarios,
      scenarios: prevState.scenarios.filter(s => s._id !== scenarioIdToDelete),
      showDeletionPrompt: false
    }));
    onChange({
      ...contextualBidding,
      scenarios: markScenarioAsRemoved(scenarioIdToDelete!)
    });
  };

  const { groups = [], items = [] } = groupsConfig;
  const availableGroups: CampaignGroups = getAvailableGroups(groups, items);

  const initialScenarioIds = useMemo(
    () => contextualBidding.scenarios.map(s => s.scenario),
    // intentionally omit contextualBidding.scenarios — key must stay stable across add/remove,
    // only recompute when the section is (re)enabled
    [contextualBidding.enabled]
  );

  const { isLoading: loadingData } = useQueryFetch<Scenario[]>(
    ['contextualBiddingScenarios', id, initialScenarioIds],
    async () => fetchScenarios({ scenarioIds: initialScenarioIds, users: [user.id] }, {}),
    {
      enabled: !!contextualBidding?.enabled,
      onSuccess: (data: Scenario[]) => {
        const cpyCurrentScenarioGroups = structuredClone(groupedScenarios);
        data.forEach(scenario => {
          const currentScenario = contextualBidding.scenarios.find(fS => fS.scenario === scenario._id);
          if (hasAccessToAddGroups && currentScenario) {
            const groupsToScenario = availableGroups.filter(aG => currentScenario[groupsType].includes(aG._id));
            cpyCurrentScenarioGroups.set(currentScenario.scenario!, groupsToScenario);
          }
        });
        setState(prevState => ({
          ...prevState,
          groupedScenarios: cpyCurrentScenarioGroups,
          scenarios: data
        }));
      }
    }
  );

  // the ONLY remaining useEffect — intentionally depends only on contextualBidding.enabled,
  // not on scenarios, so it cannot re-trigger itself
  useEffect(() => {
    if (!contextualBidding?.enabled && scenarios.length) {
      setState(prevState => ({ ...prevState, scenarios: [] }));
    }
  }, [contextualBidding?.enabled]);

  const handleClosePrompt = () => {
    setState(prevState => ({
      ...prevState,
      scenarioIdToDelete: '',
      showDeletionPrompt: false
    }));
  };

  const promptButtons: ButtonUIProps[] = [
    {
      onClick: onDeleteScenario,
      text: formatMessage({
        defaultMessage: 'Remove Context',
        id: 'contextualBidding.modal.delete.button'
      }),
      type: 'primary'
    }
  ];

  const handleLinkScenarioClick = (_id: string) => {
    setState(prevState => ({
      ...prevState,
      scenarioGroupsModal: {
        ...scenarioGroupsModal,
        id: _id,
        status: 'opened'
      }
    }));
  };

  const handleScenarioGroupsModalClose = () => {
    setState(prevState => ({
      ...prevState,
      scenarioGroupsModal: {
        ...prevState.scenarioGroupsModal,
        status: 'closed'
      }
    }));
  };

  const getCurrentGroupsIds = (scenarioId: string) => {
    const currentScenarioGroups = groupedScenarios.get(scenarioId);

    return currentScenarioGroups?.length ? currentScenarioGroups.map(group => group._id) : [];
  };

  const filteredGroups = availableGroups.filter(aG => {
    const currentGroupsIds = getCurrentGroupsIds(scenarioGroupsModal.id);
    return !currentGroupsIds?.includes(aG._id);
  });

  const formatScenariosToContextualBidding = (
    currentScenarios: Scenario[],
    currentScenarioGroups: State['groupedScenarios']
  ): ContextualBiddingInterface<string>['scenarios'] => {
    return currentScenarios.map(scenario => {
      const scenarioGroups = currentScenarioGroups?.get(scenario._id || '')?.map(group => group._id);
      return {
        scenario: scenario._id!,
        screenGroups: [],
        zoneGroups: [],
        [groupsType]: scenarioGroups || []
      };
    });
  };

  const handleScenarioGroupsSave = (groupIds: string[]) => {
    const cpyCurrentScenarioGroups = structuredClone(groupedScenarios);
    const currentGroupsIds = getCurrentGroupsIds(scenarioGroupsModal.id);
    const updatedGroupsIds = [...currentGroupsIds, ...groupIds];
    const newGroups = availableGroups.filter(availableGroup => updatedGroupsIds.includes(availableGroup._id));

    cpyCurrentScenarioGroups.set(scenarioGroupsModal.id, newGroups);

    setState(prevState => ({
      ...prevState,
      groupedScenarios: cpyCurrentScenarioGroups
    }));

    onChange({
      ...contextualBidding,
      scenarios: formatScenariosToContextualBidding(scenarios, cpyCurrentScenarioGroups)
    });

    handleScenarioGroupsModalClose();
  };

  const handleScenarioGroupsDelete = (scenarioId: string, groupId: string) => {
    const cpyCurrentScenarioGroups = structuredClone(groupedScenarios);
    const currentGroupsIds = getCurrentGroupsIds(scenarioId);
    const groupsFiltered = currentGroupsIds.filter(currentGroupId => currentGroupId !== groupId);
    const updatedGroups = availableGroups.filter(availableGroup => groupsFiltered.includes(availableGroup._id));
    cpyCurrentScenarioGroups.set(scenarioId, updatedGroups);

    setState(prevState => ({
      ...prevState,
      groupedScenarios: cpyCurrentScenarioGroups
    }));

    onChange({
      ...contextualBidding,
      scenarios: formatScenariosToContextualBidding(scenarios, cpyCurrentScenarioGroups)
    });
  };

  const handleEditScenarioSave = (editedScenario: Scenario, replace: boolean, prevScenario: Scenario) => {
    const cpyCurrentScenarioGroups = structuredClone(groupedScenarios);
    const newScenarios = scenarios.map(scenario => {
      if (replace) {
        if (scenario._id === prevScenario._id) {
          const currentScenarioGroups = getCurrentGroupsIds(prevScenario?._id || '');
          const currentGroups = availableGroups.filter(availableGroup =>
            currentScenarioGroups.includes(availableGroup._id)
          );

          cpyCurrentScenarioGroups.delete(prevScenario?._id || '');
          cpyCurrentScenarioGroups.set(editedScenario?._id || '', currentGroups);

          return editedScenario;
        }
        return scenario;
      }
      if (scenario._id === editedScenario._id) {
        return {
          ...scenario,
          ...editedScenario
        };
      }
      return scenario;
    });

    setState(prevState => ({
      ...prevState,
      groupedScenarios: cpyCurrentScenarioGroups,
      scenarios: newScenarios
    }));

    onChange({
      ...contextualBidding,
      scenarios: formatScenariosToContextualBidding(newScenarios, cpyCurrentScenarioGroups)
    });
  };

  const entityTypeText = formatMessage(
    {
      defaultMessage: 'insertion order',
      id: `multiFlightCommon.entityTypes.${entityType}`
    },
    { value: 1 }
  );

  const deletePrompt = () => {
    return ReactDOM.createPortal(
      <CustomThemeProvider themeContrast={theme}>
        <Prompt
          buttons={promptButtons}
          closePrompt={handleClosePrompt}
          description={formatMessage(
            {
              defaultMessage: 'If you remove this context, the {entityType} will operate without it.',
              id: 'contextualBidding.modal.delete.subtitle'
            },
            {
              entityType: entityTypeText
            }
          )}
          icon="fas fa-exclamation-triangle"
          title={formatMessage(
            {
              defaultMessage: 'Are you sure you want to remove this context from your {entityType}?',
              id: 'contextualBidding.modal.delete.title'
            },
            {
              entityType: entityTypeText
            }
          )}
        />
      </CustomThemeProvider>,
      targetBodyPrompt
    );
  };

  const radioButtonsItems = [
    {
      icons: [
        {
          icon: 'fa-regular fa-circle-check',
          position: 'left' as const
        }
      ],
      id: 'with_context',
      text: formatMessage({
        defaultMessage: 'All With Context',
        id: 'contextualBidding.inventoryDistribution.radioButton.withContext'
      })
    },
    {
      icons: [
        {
          icon: 'fa-regular fa-circle-xmark',
          position: 'left' as const
        }
      ],
      id: 'with_no_context',
      text: formatMessage({
        defaultMessage: 'All With No Context',
        id: 'contextualBidding.inventoryDistribution.radioButton.withoutContext'
      })
    },
    {
      id: 'balanced',
      text: formatMessage({
        defaultMessage: 'Balanced',
        id: 'contextualBidding.inventoryDistribution.radioButton.balanced'
      })
    }
  ];

  const handleRangeChange = (value: any) => {
    const matched = (Object.entries(valueByRadioButton).find(([, v]) => v === value)?.[0] as RadioButton) || '';
    setUiState(prev => ({ ...prev, selectedRadioButton: matched }));
    onChange({
      ...contextualBidding,
      distribution: {
        enabled: true,
        value
      }
    });
  };

  const handleRadioButtonChange = (value: RadioButton) => {
    setUiState(prev => ({ ...prev, rangeKey: prev.rangeKey + 1, selectedRadioButton: value }));
    onChange({
      ...contextualBidding,
      distribution: {
        enabled: true,
        value: valueByRadioButton[value]
      }
    });
  };

  const handleResetContextualBidding = () => {
    const resetScenarios = scenarios.map(scenario => ({
      ...scenario,
      removed: true
    }));
    setState({
      ...DEFAULT_STATE_VALUE,
      scenarios: resetScenarios
    });
    setUiState(prev => ({ ...prev, selectedRadioButton: '' }));
    onChange({
      ...contextualBidding,
      distribution: {
        enabled: false,
        value: valueByRadioButton.with_context
      },
      enabled: false,
      scenarios: resetScenarios.map(({ _id }) => ({
        removed: true,
        scenario: _id!,
        screenGroups: [],
        zoneGroups: []
      }))
    });
  };

  const isSectionOpened = contextualBidding?.enabled;
  const contextualBiddingTags = getContextualBiddingTags(contextualBidding, goal);
  const noneDeletedContextualBiddingScenariosIds = contextualBidding.scenarios
    .filter(s => !s.removed)
    .map(s => s.scenario);
  const hasScenarios = noneDeletedContextualBiddingScenariosIds.length > 0;

  return (
    <ContextualBiddingStyled id={id}>
      <FormExpandableSection
        defaultOpen={isSectionOpened}
        onReset={handleResetContextualBidding}
        preffixId={id}
        tags={contextualBiddingTags}
        title={formatMessage({
          defaultMessage: 'Contextual Bidding',
          id: 'contextSystem.contextualBiddingSection.title'
        })}
        tooltipText={formatMessage({
          defaultMessage:
            'Dynamically adjust your bids based on specific keywords or contextual signals found on the page.',
          id: 'contextSystem.contextualBiddingSection.tooltip'
        })}
      >
        <div className="contextualBiddingSection__content" id={'contextualBiddingErrorTarget'}>
          <Switch>
            <Switch.Case condition={loadingData}>
              <LoadingSkeleton count={3} height={50} />
            </Switch.Case>
            <Switch.Default>
              <div
                className={cn('contextualBiddingSection__contextList', {
                  'contextualBiddingSection__contextList--noScenario': !hasScenarios
                })}
              >
                <div className="contextualBiddingSection__actionSection">
                  {hasScenarios ? (
                    <InputSearch
                      className="contextualBiddingSection__actionSectionInputText"
                      id="scenarioList_textFilter"
                      lens
                      name="scenarioList_textFilter"
                      onChange={text => setUiState(prev => ({ ...prev, inputText: text }))}
                      placeholder={formatMessage({
                        defaultMessage: 'Search by name...',
                        id: 'contextualBidding.input.text'
                      })}
                      tiny
                    />
                  ) : null}
                  <ContextualMenu id={`contextualMenu-${id}`} placement="bottom">
                    <ContextualMenu.Trigger>
                      <ButtonUI
                        icon="fa-regular fa-plus"
                        iconPosition="left"
                        id={`contextualMenuTrigger-${id}`}
                        shape={hasScenarios ? 'squareRounded' : 'link'}
                        size="ultraSmall"
                        text={formatMessage({
                          defaultMessage: 'Add Context',
                          id: 'contextualBidding.buttons.add'
                        })}
                        type="secondary"
                      />
                    </ContextualMenu.Trigger>
                    <ContextualMenu.Item
                      id={`selectButton-${id}`}
                      onClick={handleSelectContentClick}
                      text={formatMessage({
                        defaultMessage: 'Select Existing Context',
                        id: 'contextualBidding.contextual.select'
                      })}
                    />
                    {hasPermissionsToCreate && (
                      <ContextualMenu.Item
                        id={`createButton-${id}`}
                        onClick={() => handleScenarioCreationToggle('opened')}
                        text={formatMessage({
                          defaultMessage: 'Create New Context',
                          id: 'contextualBidding.contextual.create'
                        })}
                      />
                    )}
                  </ContextualMenu>
                </div>
                {hasScenarios ? (
                  <List
                    campaignType={campaign.type}
                    groupedScenarios={groupedScenarios}
                    id={`${id}__contextualBiddingListItem`}
                    isAllowedToAddGroups={hasAccessToAddGroups}
                    isAllowedToEditScenarios={hasAccessToEditScenarios}
                    onClickDelete={onDeleteHandler}
                    onClickEdition={handleEditionClick}
                    onClickLink={handleLinkScenarioClick}
                    onClickView={onViewScenario}
                    onGroupDelete={handleScenarioGroupsDelete}
                    scenarios={scenarios.filter(
                      s =>
                        noneDeletedContextualBiddingScenariosIds.includes(s._id!) &&
                        s.name.toLowerCase().includes(inputText.toLocaleLowerCase())
                    )}
                  />
                ) : null}
              </div>
            </Switch.Default>
          </Switch>
          {hasAccessToContextualBiddingInventoryDistribution ? (
            <div className="contextualBiddingSection__distribution">
              {!hasScenarios ? <div className="contextualBiddingSection__distributionVeil" /> : null}
              <Range
                className="contextualBiddingSection__range"
                id="contextualBiddingSection__inventory"
                key={rangeKey}
                max={goal}
                min={0}
                onChange={handleRangeChange}
                type="slider"
                value={rangeValue}
              >
                <Range.Title>
                  <Range.Title.Left className="contextualBiddingSection__rangeTitleLeft">
                    {formatMessage({
                      defaultMessage: 'Inventory bidding with NO CONTEXT',
                      id: 'contextualBidding.inventoryDistribution.range.withoutContext'
                    })}
                  </Range.Title.Left>
                  <Range.Title.Right className="contextualBiddingSection__rangeTitleRight">
                    {formatMessage({
                      defaultMessage: 'Inventory bidding with CONTEXT',
                      id: 'contextualBidding.inventoryDistribution.range.withContext'
                    })}
                  </Range.Title.Right>
                </Range.Title>
                <Range.SliderInput />
                <Range.Info>
                  <Range.Info.Center className="contextualBiddingSection__rangeInfoCenter">
                    <span className="contextualBiddingSection__rangeInfoCenterText">
                      {formatMessage({
                        defaultMessage: 'Total Budget:',
                        id: 'contextualBidding.inventoryDistribution.totalBudget'
                      })}
                    </span>
                    <span>
                      {currencyInfo
                        ? normalizeWithUnit(goal, currencyInfo, 2)
                        : normalize(goal, 0, user.currencyCountry)}
                    </span>
                  </Range.Info.Center>
                </Range.Info>
              </Range>
              <RadioButtonsGroup
                className="contextualBiddingSection__radioButtons"
                id="mobileDeviceAndConnectivityRadioButtonListDeviceType"
                items={radioButtonsItems}
                key={selectedRadioButton}
                onChange={(value: any) => handleRadioButtonChange(value as RadioButton)}
                selectedItem={selectedRadioButton}
              />
            </div>
          ) : null}
        </div>
      </FormExpandableSection>
      <ModalScenarios
        campaignId={campaign._id}
        campaignType={campaign.type}
        country={countries.length > 1 ? 'allCountries' : countries[0]}
        devicePlatforms={campaign.devicePlatforms}
        isModalOpened={scenariosSelectorOpen}
        onModalClose={onSelectScenarioModalClose}
        onModalSave={onSaveSelectedContext}
        selectedScenariosIds={noneDeletedContextualBiddingScenariosIds || []}
        user={user as UserInterface}
        userRoles={userRoles}
      />
      <ScenarioModalViewer
        campaignId={campaign._id}
        isModalOpened={scenarioViewerOpen}
        onModalClose={onViewScenarioModalClose}
        scenario={scenarioSelected}
        user={user as UserInterface}
        userRoles={userRoles}
      />
      {scenarioSelected ? (
        <ScenarioModalEdit
          campaign={campaign}
          campaignType={campaign.type}
          devicePlatforms={campaign.devicePlatforms}
          isModalOpened={scenarioEditionOpen}
          onModalClose={handleEditScenarioModalClose}
          onModalSave={handleEditScenarioSave}
          scenario={scenarioSelected}
          user={user as UserInterface}
          userRoles={userRoles}
        />
      ) : null}
      <ModalGroups
        campaignType={campaign.type}
        groups={filteredGroups}
        id={`${id}__scenarioModalGroups`}
        isModalOpened={scenarioGroupsModal.status === 'opened'}
        onModalClose={handleScenarioGroupsModalClose}
        onModalSave={handleScenarioGroupsSave}
      />
      <ScenarioModalCreator
        campaignId={campaign._id}
        campaignType={campaign.type}
        country={countries.length > 1 ? 'allCountries' : countries[0]}
        devicePlatforms={campaign.devicePlatforms}
        isModalOpened={scenarioCreationOpen}
        onModalClose={() => handleScenarioCreationToggle('closed')}
        onModalSave={handleScenarioCreationSave}
        user={user as UserInterface}
        userRoles={userRoles}
      />
      {showDeletionPrompt && deletePrompt()}
    </ContextualBiddingStyled>
  );
}
